import type {
  BrowserKinetic,
  TransportRequest,
  VisualizationEvent,
} from '../../types/simulationPlayback';
import {
  findTransferDwellForEvent,
  findTransferStopLeg,
  isOhtCarrierTransferEvent,
  MAX_TRANSFER_ANIMATION_S,
  resolveTransferWindow,
  TRANSFER_VISUAL_FRACS,
} from '../simulationPlayback';
import {
  buildReplayPlan,
  type ReplayPlan,
} from './buildReplayPlan';
import {
  seedPortInventoryFromFirstEvent,
  type FoupOwnershipRecord,
} from './foupOwnershipLedger';

type DraftEvent = Omit<VisualizationEvent, 'sequence'> & {
  /** Deterministic ordering for records at the same timestamp. */
  order: number;
};

function requestFoupId(
  requestId: string,
  requestsById: ReadonlyMap<string, TransportRequest>
): string {
  return requestsById.get(requestId)?.foupId || `${requestId}:FOUP`;
}

/**
 * Compile the legacy finalSimulation events into the same ordered physical
 * timeline consumed by ReplayCoordinator.
 *
 * This is intentionally the only compatibility boundary. 3D consumers read
 * ReplayFrameSnapshot regardless of whether the backend supplied native
 * visualizationEvents or only the legacy events/vehicleLegs contract.
 */
export function compileLegacyReplayPlan(
  browser: BrowserKinetic,
  transportRequests: readonly TransportRequest[]
): ReplayPlan {
  const drafts: DraftEvent[] = [];
  const requestsById = new Map(
    transportRequests.map((request) => [request.requestId, request] as const)
  );
  const initialOwnershipByFoup = new Map<string, FoupOwnershipRecord>();
  // Earliest DROP per FOUP. A carrier that is first delivered must not be
  // pre-placed in the stocker just because a later PICKUP will retrieve it.
  const firstDropTByFoup = new Map<string, number>();
  const carrierEvents = [...browser.events]
    .filter((event) => isOhtCarrierTransferEvent(event) && event.portId)
    .sort((a, b) => a.t - b.t || 0);
  const initialPortInventory = seedPortInventoryFromFirstEvent(carrierEvents);
  for (const event of carrierEvents) {
    const foupId = requestFoupId(event.requestId, requestsById);
    if (event.type.toUpperCase() === 'DROP' && !firstDropTByFoup.has(foupId)) {
      firstDropTByFoup.set(foupId, event.t);
    }
  }
  let order = 0;

  const push = (event: Omit<DraftEvent, 'order'>): void => {
    drafts.push({ ...event, order: order++ });
  };

  // Event-defined route and wait spans. Geometry still comes from the matching
  // segmentIds/segmentWindows; these records isolate it by request and OHT.
  for (const leg of browser.apiLegs ?? []) {
    const operationId = `${leg.requestId}:${leg.ohtId}:route:${leg.startSec}`;
    const isWait = leg.phase === 'process_wait' || (leg.waitS ?? 0) > 0;
    push({
      eventId: `${operationId}:${isWait ? 'wait' : 'route'}`,
      operationId,
      kind: isWait ? 'WAIT' : 'OHT_ROUTE',
      phase: isWait ? 'wait' : 'route',
      intent: isWait ? 'status' : 'physical',
      t: leg.startSec,
      endT: Math.max(leg.startSec, leg.endSec),
      requestId: leg.requestId,
      ohtId: leg.ohtId,
      fromPortId: leg.fromPortId,
      toPortId: leg.toPortId,
      segmentId: leg.segmentIds[0],
      reason: leg.reason,
    });
  }

  for (const [connectorIndex, use] of (browser.connectorUses ?? []).entries()) {
    const junctionId = use.switchId ?? use.connectorId;
    if (!junctionId || !use.ohtId) continue;
    const start = use.startSec ?? 0;
    const end = Math.max(start + 0.001, use.endSec ?? start + 1);
    const duration = end - start;
    const operationId = `${use.requestId ?? 'junction'}:${use.ohtId}:${junctionId}:${connectorIndex}`;
    push({
      eventId: `${operationId}:enter`,
      operationId,
      kind: use.switchId ? 'SWITCH' : 'JUNCTION',
      phase: 'junction_enter',
      intent: 'physical',
      t: start,
      endT: start + duration * 0.15,
      requestId: use.requestId,
      ohtId: use.ohtId,
      junctionId,
      connectorId: use.connectorId,
    });
    push({
      eventId: `${operationId}:rotate`,
      operationId,
      kind: use.switchId ? 'SWITCH' : 'JUNCTION',
      phase: 'junction_rotate',
      intent: 'physical',
      t: start + duration * 0.15,
      endT: start + duration * 0.85,
      requestId: use.requestId,
      ohtId: use.ohtId,
      junctionId,
      connectorId: use.connectorId,
    });
    push({
      eventId: `${operationId}:clear`,
      operationId,
      kind: use.switchId ? 'SWITCH' : 'JUNCTION',
      phase: 'junction_clear',
      intent: 'physical',
      t: start + duration * 0.85,
      endT: end,
      requestId: use.requestId,
      ohtId: use.ohtId,
      junctionId,
      connectorId: use.connectorId,
    });
  }

  browser.events.forEach((event, eventIndex) => {
    const type = event.type.toUpperCase();
    const operationId = `${event.requestId}:${event.ohtId}:${eventIndex}`;

    if (type === 'WAIT') {
      const waitEnd = Math.max(event.t, event.untilS ?? event.t + (event.waitS ?? 0));
      push({
        eventId: `${operationId}:wait`,
        operationId,
        kind: 'WAIT',
        phase: 'wait',
        intent: 'status',
        t: event.t,
        endT: waitEnd,
        requestId: event.requestId,
        ohtId: event.ohtId,
        portId: event.portId,
        reason: event.reason,
      });
      const processStation = event.portId
        ? browser.toolPortOwners?.[event.portId]
        : undefined;
      if (processStation) {
        push({
          eventId: `${operationId}:process`,
          operationId,
          kind: 'EQUIPMENT_PROCESS',
          phase: 'process',
          intent: 'physical',
          t: event.t,
          endT: waitEnd,
          requestId: event.requestId,
          portId: event.portId,
          stationId: processStation,
          reason: event.reason,
        });
      }
      return;
    }

    if (event.junctionId || event.connectorId) {
      const junctionId = event.junctionId ?? event.connectorId!;
      const endT = Math.max(event.t + 0.001, event.untilS ?? event.t + (event.waitS ?? 1));
      push({
        eventId: `${operationId}:junction`,
        operationId,
        kind: event.connectorId ? 'SWITCH' : 'JUNCTION',
        phase: 'junction_rotate',
        intent: 'physical',
        t: event.t,
        endT,
        requestId: event.requestId,
        ohtId: event.ohtId,
        junctionId,
        connectorId: event.connectorId,
        segmentId: event.segmentId,
      });
    }

    if (['AVAILABLE', 'OCCUPY', 'OCCUPIED', 'BUSY', 'READY'].includes(type)) {
      const stationId = event.portId
        ? browser.stockerPortOwners?.[event.portId] ??
          browser.toolPortOwners?.[event.portId]
        : undefined;
      push({
        eventId: `${operationId}:status`,
        operationId,
        kind: 'STATUS_CHANGE',
        phase: 'status_change',
        intent: 'status',
        t: event.t,
        requestId: event.requestId,
        ohtId: event.ohtId,
        portId: event.portId,
        stationId,
        status: type === 'OCCUPIED' ? 'Occupy' : `${type[0]}${type.slice(1).toLowerCase()}`,
      });
      return;
    }

    if (type !== 'PICKUP' && type !== 'DROP') return;
    if (!isOhtCarrierTransferEvent(event)) return;

    // Legacy carrier events are authoritative by action + ohtId. Backend
    // inventory reconciliation can report delta:0 after moving a processed FOUP
    // between tool ports, but the OHT PICKUP/DROP still has to animate and
    // update visual ownership. Port-only process events were rejected above.
    const delta = type === 'PICKUP' ? -1 : 1;

    const portId = event.portId;
    if (!portId) return;
    const stockerId = browser.stockerPortOwners?.[portId];
    const equipmentId = browser.toolPortOwners?.[portId];
    const stationId = stockerId ?? equipmentId;

    const foupId = requestFoupId(event.requestId, requestsById);
    const legs = browser.vehicleLegs.legsByVehicle[event.ohtId];
    const hasMotionLegs = Array.isArray(legs) && legs.length > 0;
    const dwell = findTransferDwellForEvent(event, legs);
    const action = type as 'PICKUP' | 'DROP';
    // Never pre-place a FOUP in a stocker. The rack stays empty until an
    // OHT DROP commits; later drops increment the count.
    if (action === 'PICKUP' && !stockerId && !initialOwnershipByFoup.has(foupId)) {
      const priorDropT = firstDropTByFoup.get(foupId);
      if (priorDropT === undefined || priorDropT > event.t) {
        initialOwnershipByFoup.set(foupId, {
          foupId,
          ownerKind: 'port',
          ownerId: portId,
          portId,
          stationId,
          sinceSequence: Number.NEGATIVE_INFINITY,
          sinceT: 0,
        });
      }
    }
    // Pickup stamped after the OHT left the stocker: do not invent a 9s empty
    // hoist on a moving vehicle. Ownership flips now so the FOUP rides along.
    if (type === 'PICKUP' && hasMotionLegs && !dwell) {
      push({
        eventId: `${operationId}:in-transit-complete`,
        operationId,
        kind: 'OHT_PICKUP',
        phase: 'complete',
        intent: 'physical',
        action: 'PICKUP',
        t: event.t,
        requestId: event.requestId,
        ohtId: event.ohtId,
        foupId,
        portId,
        stationId,
        delta,
      });
      push({
        eventId: `${operationId}:in-transit-inventory`,
        operationId,
        kind: 'INVENTORY_COMMIT',
        phase: 'inventory_commit',
        intent: 'physical',
        action: 'PICKUP',
        t: event.t,
        requestId: event.requestId,
        ohtId: event.ohtId,
        foupId,
        portId,
        stationId,
        delta: -Math.abs(delta),
        inventoryBefore: event.inventoryBefore,
        inventoryAfter: event.inventoryAfter,
      });
      return;
    }
    // Tools park on a track node, so their stop leg is not keyed by the load
    // port and `dwell` is often null. Resolve the node separately rather than
    // dropping both the handoff point and the arrival anchor.
    const stopLeg = equipmentId ? dwell ?? findTransferStopLeg(event, legs) : dwell;
    const window = stopLeg
      ? resolveTransferWindow(event, stopLeg)
      : { startS: event.t, durationS: MAX_TRANSFER_ANIMATION_S };
    const start = Math.max(event.t, window.startS);
    const duration = Math.max(0.5, window.durationS);
    const end = start + duration;
    const handoff =
      stopLeg &&
      Number.isFinite(stopLeg.toX) &&
      Number.isFinite(stopLeg.toY)
        ? { handoffX: stopLeg.toX, handoffY: stopLeg.toY }
        : {};
    // Equipment transfers have a dedicated stopped-at-node phase. The actual
    // shutter/hoist operation receives its own operation window afterward, so
    // normalized progress starts at zero only once the OHT is fully stationary.
    const equipmentSettleS = equipmentId
      ? Math.min(1, Math.max(0, duration - 0.5))
      : 0;
    const transferStart = start + equipmentSettleS;
    const transferDuration = Math.max(0.5, end - transferStart);
    const equipmentHandoff = { ...handoff, preSettled: true };

    // Entry / Exit gate ports (LP-ENTRY / LP-EXIT) hang off track nodes, so no
    // stocker or tool owns them and there is no tray for the hoist to reach —
    // lowering the legs there would drop them the full rail height to the floor.
    // The carrier changes hands instantly instead: no span event, so no lifter
    // sequence runs, but ownership still moves and the OHT leaves the gate
    // visibly loaded rather than letting the FOUP reappear at the next station.
    if (!stationId) {
      push({
        eventId: `${operationId}:gate-complete`,
        operationId,
        kind: action === 'PICKUP' ? 'OHT_PICKUP' : 'OHT_DROP',
        phase: 'complete',
        intent: 'physical',
        action,
        t: start,
        requestId: event.requestId,
        ohtId: event.ohtId,
        foupId,
        portId,
        delta,
      });
      push({
        eventId: `${operationId}:gate-inventory`,
        operationId,
        kind: 'INVENTORY_COMMIT',
        phase: 'inventory_commit',
        intent: 'physical',
        action,
        t: start,
        requestId: event.requestId,
        ohtId: event.ohtId,
        foupId,
        portId,
        delta: action === 'PICKUP' ? -Math.abs(delta) : Math.abs(delta),
        inventoryBefore: event.inventoryBefore,
        inventoryAfter: event.inventoryAfter,
      });
      return;
    }

    push({
      eventId: `${operationId}:arrive`,
      operationId: equipmentId ? `${operationId}:arrival` : operationId,
      kind: 'OHT_ARRIVE',
      phase: 'arrive',
      intent: 'physical',
      t: start,
      endT: equipmentId
        ? Math.max(start + 0.05, transferStart)
        : start + Math.max(0.05, duration * 0.05),
      requestId: event.requestId,
      ohtId: event.ohtId,
      foupId,
      portId,
      stationId,
      action,
    });

    if (stockerId) {
      if (action === 'DROP') {
        const ohtEnd = start + duration * 0.35;
        const craneEnd = start + duration * 0.95;
        push({
          eventId: `${operationId}:oht-drop`,
          operationId,
          kind: 'OHT_DROP',
          phase: 'handoff',
          intent: 'physical',
          action,
          t: start + duration * 0.05,
          endT: ohtEnd,
          requestId: event.requestId,
          ohtId: event.ohtId,
          foupId,
          portId,
          stationId,
          delta,
        });
        push({
          eventId: `${operationId}:oht-drop-complete`,
          operationId,
          kind: 'OHT_DROP',
          phase: 'complete',
          intent: 'physical',
          action,
          t: ohtEnd,
          requestId: event.requestId,
          ohtId: event.ohtId,
          foupId,
          portId,
          stationId,
          delta,
        });
        push({
          eventId: `${operationId}:crane-store`,
          operationId,
          kind: 'STOCKER_TRANSFER',
          phase: 'crane_move',
          intent: 'internal',
          action,
          t: ohtEnd,
          endT: craneEnd,
          requestId: event.requestId,
          foupId,
          portId,
          fromPortId: portId,
          slotId: `${stockerId}:slot:${event.inventoryAfter ?? 'next'}`,
          stationId: stockerId,
        });
        push({
          eventId: `${operationId}:rack-complete`,
          operationId,
          kind: 'INTERNAL_TRANSFER',
          phase: 'complete',
          intent: 'internal',
          action,
          t: craneEnd,
          requestId: event.requestId,
          foupId,
          portId,
          fromPortId: portId,
          slotId: `${stockerId}:slot:${event.inventoryAfter ?? 'next'}`,
          stationId: stockerId,
        });
        push({
          eventId: `${operationId}:inventory`,
          operationId,
          kind: 'INVENTORY_COMMIT',
          phase: 'inventory_commit',
          intent: 'internal',
          action,
          t: craneEnd,
          requestId: event.requestId,
          foupId,
          portId,
          stationId: stockerId,
          delta: Math.abs(delta),
          inventoryBefore: event.inventoryBefore,
          inventoryAfter: event.inventoryAfter,
        });
      } else {
        const craneEnd = start + duration * TRANSFER_VISUAL_FRACS.pickupStockerCommit;
        const ohtGrip = start + duration * TRANSFER_VISUAL_FRACS.pickupOhtHandoff;
        const ohtEnd = start + duration * 0.9;
        const slotId = `${stockerId}:slot:${event.inventoryBefore ?? 'source'}`;
        push({
          eventId: `${operationId}:crane-retrieve`,
          operationId,
          kind: 'STOCKER_TRANSFER',
          phase: 'crane_move',
          intent: 'internal',
          action,
          t: start,
          endT: craneEnd,
          requestId: event.requestId,
          foupId,
          portId,
          toPortId: portId,
          slotId,
          stationId: stockerId,
        });
        push({
          eventId: `${operationId}:port-ready`,
          operationId,
          kind: 'INTERNAL_TRANSFER',
          phase: 'complete',
          intent: 'internal',
          action,
          t: craneEnd,
          requestId: event.requestId,
          foupId,
          portId,
          toPortId: portId,
          slotId,
          stationId: stockerId,
        });
        push({
          eventId: `${operationId}:inventory`,
          operationId,
          kind: 'INVENTORY_COMMIT',
          phase: 'inventory_commit',
          intent: 'internal',
          action,
          t: craneEnd,
          requestId: event.requestId,
          foupId,
          portId,
          stationId: stockerId,
          delta: -Math.abs(delta),
          inventoryBefore: event.inventoryBefore,
          inventoryAfter: event.inventoryAfter,
        });
        push({
          eventId: `${operationId}:oht-pickup`,
          operationId,
          kind: 'OHT_PICKUP',
          phase: 'handoff',
          intent: 'physical',
          action,
          t: craneEnd,
          endT: ohtEnd,
          requestId: event.requestId,
          ohtId: event.ohtId,
          foupId,
          portId,
          stationId,
          delta,
        });
        push({
          eventId: `${operationId}:oht-pickup-complete`,
          operationId,
          kind: 'OHT_PICKUP',
          phase: 'complete',
          intent: 'physical',
          action,
          t: ohtGrip,
          requestId: event.requestId,
          ohtId: event.ohtId,
          foupId,
          portId,
          stationId,
          delta,
        });
      }
      return;
    }

    const equipmentEnd = transferStart + transferDuration;
    const openEnd = transferStart + transferDuration * 0.18;
    const handoffEnd = transferStart + transferDuration * 0.78;
    push({
      eventId: `${operationId}:shutter-open`,
      operationId,
      kind: 'EQUIPMENT_TRANSFER',
      phase: 'shutter_open',
      intent: 'physical',
      action,
      t: transferStart,
      endT: openEnd,
      requestId: event.requestId,
      ohtId: event.ohtId,
      foupId,
      portId,
      stationId: equipmentId,
      ...equipmentHandoff,
    });
    push({
      eventId: `${operationId}:equipment-handoff`,
      operationId,
      kind: 'EQUIPMENT_TRANSFER',
      phase: 'handoff',
      intent: 'physical',
      action,
      t: openEnd,
      endT: handoffEnd,
      requestId: event.requestId,
      ohtId: event.ohtId,
      foupId,
      portId,
      stationId: equipmentId,
      delta,
      ...equipmentHandoff,
    });
    push({
      eventId: `${operationId}:oht-${action.toLowerCase()}-complete`,
      operationId,
      kind: action === 'PICKUP' ? 'OHT_PICKUP' : 'OHT_DROP',
      phase: 'complete',
      intent: 'physical',
      action,
      t: handoffEnd,
      requestId: event.requestId,
      ohtId: event.ohtId,
      foupId,
      portId,
      stationId: equipmentId,
      delta,
      ...equipmentHandoff,
    });
    push({
      eventId: `${operationId}:inventory`,
      operationId,
      kind: 'INVENTORY_COMMIT',
      phase: 'inventory_commit',
      intent: 'physical',
      action,
      t: handoffEnd,
      requestId: event.requestId,
      foupId,
      portId,
      stationId: equipmentId,
      delta: action === 'PICKUP' ? -Math.abs(delta) : Math.abs(delta),
      inventoryBefore: event.inventoryBefore,
      inventoryAfter: event.inventoryAfter,
      ...equipmentHandoff,
    });
    push({
      eventId: `${operationId}:shutter-close`,
      operationId,
      kind: 'EQUIPMENT_TRANSFER',
      phase: 'shutter_close',
      intent: 'physical',
      action,
      t: handoffEnd,
      endT: equipmentEnd,
      requestId: event.requestId,
      ohtId: event.ohtId,
      foupId,
      portId,
      stationId: equipmentId,
      ...equipmentHandoff,
    });
  });

  const sorted = drafts
    .sort((a, b) => a.t - b.t || a.order - b.order)
    .map((draft, sequence) => {
      const { order: draftOrder, ...event } = draft;
      void draftOrder;
      return { ...event, sequence };
    });

  return buildReplayPlan(sorted, {
    version: 1,
    durationS: browser.durationS,
    ledgerOptions: {
      initialPortInventory,
      initialOwnership: [...initialOwnershipByFoup.values()],
    },
  });
}
